import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import PrizeModal from '../components/PrizeModal';
import CampaignQR from '../components/CampaignQR';
import CampaignIntegrationModal from '../components/CampaignIntegrationModal';
import CampaignDesignModal from '../components/CampaignDesignModal';
import { BASE_URL, fetchWithAuth } from '../services/api';
import { Trash2, Palette, Sparkles } from 'lucide-react';

// Countdown Timer Component
const CountdownTimer = ({ scheduledTime, onExpire }) => {
    const [timeLeft, setTimeLeft] = useState('');

    useEffect(() => {
        let expired = false;
        const updateTimer = () => {
            if (expired) return;
            const now = new Date();
            const target = new Date(scheduledTime);
            const diff = target - now;

            if (diff <= 0) {
                expired = true;
                setTimeLeft('Deleting...');
                if (onExpire) onExpire();
                return;
            }

            const minutes = Math.floor(diff / 60000);
            const seconds = Math.floor((diff % 60000) / 1000);
            setTimeLeft(`${minutes}:${seconds.toString().padStart(2, '0')}`);
        };

        updateTimer();
        const interval = setInterval(updateTimer, 1000);

        return () => {
            expired = true;
            clearInterval(interval);
        };
    }, [scheduledTime, onExpire]);

    return (
        <span className="font-mono text-red-600 font-semibold">
            {timeLeft}
        </span>
    );
};

const CampaignsPage = () => {
    console.log('CampaignsPage Component Rendered');
    const { user } = useAuth();
    const [campaigns, setCampaigns] = useState(null);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [selectedCampaignForQR, setSelectedCampaignForQR] = useState(null);
    const [selectedCampaignForDesign, setSelectedCampaignForDesign] = useState(null);
    const [newCampaign, setNewCampaign] = useState({
        name: '',
        campaign_type: 'spin',
        start_date: '',
        end_date: '',
        max_claims: 0,
        max_spins_per_ip: 50,
        show_social_page:true,
        instagram_link:'',
        facebook_link:'',
        guidelines:'',
        is_in_store:false
    });

    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingCampaign, setEditingCampaign] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Helper function to convert UTC datetime to local datetime string for datetime-local input
    const convertUTCToLocal = (utcDateString) => {
        const date = new Date(utcDateString);
        // Get local date and format as YYYY-MM-DDTHH:mm for datetime-local input
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };



    // Fetch campaigns
    const fetchCampaigns = useCallback(async () => {
        try {
            const response = await fetchWithAuth(`${BASE_URL}/campaigns/`);
            const data = await response.json();
            // Sort campaigns by ID in descending order (newest first)
            // Create a new array to ensure React detects the change
            const sortedData = [...data].sort((a, b) => b.id - a.id);
            setCampaigns(sortedData);
            console.log('Campaigns:', sortedData);
        } catch (error) {
            console.error('Error fetching campaigns:', error);
        }
    }, []); // Empty dependency array means this function reference stays stable

    const handleEditCampaign = async (e) => {
        e.preventDefault();
        if (isSubmitting) return; // Prevent double submission

        setIsSubmitting(true);
        try {
            // Clean up the data before sending
            const campaignData = {
                ...editingCampaign,
                instagram_link: editingCampaign.instagram_link || '',
                facebook_link: editingCampaign.facebook_link || '',
                guidelines: editingCampaign.guidelines || '',
                // Convert local datetime to ISO string with timezone
                start_date: new Date(editingCampaign.start_date).toISOString(),
                end_date: new Date(editingCampaign.end_date).toISOString()
            };

            console.log('Updating campaign with data:', campaignData);

            const response = await fetchWithAuth(`${BASE_URL}/campaigns/${editingCampaign.id}/`, {
                method: 'PUT',
                body: JSON.stringify(campaignData)
            });

            console.log('Response status:', response.status);

            if (!response.ok) {
                const errorData = await response.json().catch(() => null);
                console.error('Update failed with error:', errorData);

                // Format error messages from validation errors
                let errorMessage = 'Failed to update campaign';
                if (errorData) {
                    if (errorData.detail) {
                        errorMessage = errorData.detail;
                    } else if (errorData.message) {
                        errorMessage = errorData.message;
                    } else if (typeof errorData === 'object') {
                        // Handle field-specific validation errors
                        const errors = Object.entries(errorData)
                            .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
                            .join('\n');
                        errorMessage = errors || 'Failed to update campaign';
                    }
                }

                throw new Error(errorMessage);
            }

            setEditModalOpen(false);
            setEditingCampaign(null);
            fetchCampaigns(); // Refresh the list
        } catch (error) {
            console.error('Error updating campaign:', error);
            alert(`Failed to update campaign: ${error.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleManagePrizes = (campaign) => {
        setSelectedCampaign(campaign);
    };

    // Create campaign
    const handleCreateCampaign = async (e) => {
        e.preventDefault();
        if (isSubmitting) return; // Prevent double submission

        setIsSubmitting(true);
        try {
            const { vendor, ...campaignData } = newCampaign;

            // Convert local datetime to ISO string with timezone
            const dataToSend = {
                ...campaignData,
                start_date: new Date(campaignData.start_date).toISOString(),
                end_date: new Date(campaignData.end_date).toISOString()
            };

            console.log('Sending campaign data:', dataToSend);

            const response = await fetchWithAuth(`${BASE_URL}/campaigns/`, {
                method: 'POST',
                body: JSON.stringify(dataToSend)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to create campaign');
            }

            const data = await response.json();
            console.log('Campaign created:', data);
            setIsCreateModalOpen(false);
            fetchCampaigns();
            setNewCampaign({
                name: '',
                campaign_type: 'spin',
                start_date: '',
                end_date: '',
                max_claims: 0,
                max_spins_per_ip: 50,
                show_social_page: true,
                instagram_link: '',
                facebook_link: '',
                guidelines: '',
                is_in_store: false
            });
        } catch (err) {
            console.error('Error creating campaign:', err);
            alert(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };


    const handleDeleteCampaign = async (campaignId) => {
        // Show confirmation dialog
        if (!window.confirm('Are you sure you want to delete this campaign? You will have 5 minutes to cancel.')) {
            return;
        }

        try {
            const response = await fetchWithAuth(`${BASE_URL}/campaigns/${campaignId}/`, {
                method: 'DELETE'
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('Delete failed:', response.status, errorText);
                throw new Error(`Failed to schedule campaign for deletion (${response.status}): ${errorText}`);
            }

            const data = await response.json();
            console.log('Campaign scheduled for deletion:', data);

            // Refresh campaigns list to show the scheduled deletion
            fetchCampaigns();

        } catch (error) {
            console.error('Error deleting campaign:', error);
            alert('Failed to schedule campaign for deletion');
        }
    };

    const handleCancelDeletion = async (campaignId) => {
        try {
            const response = await fetchWithAuth(`${BASE_URL}/campaigns/${campaignId}/cancel_deletion/`, {
                method: 'POST'
            });

            if (!response.ok) {
                throw new Error('Failed to cancel deletion');
            }

            // Refresh campaigns list
            fetchCampaigns();
        } catch (error) {
            console.error('Error cancelling deletion:', error);
            alert('Failed to cancel deletion');
        }
    };

    useEffect(() => {
        fetchCampaigns();
    }, []);

    if (!campaigns) {
        return <div>Loading...</div>;
    }

    return (
        <div className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
                <h1 className="text-xl sm:text-2xl font-semibold">Campaigns</h1>
                <button
                    onClick={() => setIsCreateModalOpen(true)}
                    className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 text-sm sm:text-base"
                >
                    Create Campaign
                </button>
            </div>

            <div className="grid gap-4">
    {campaigns.map((campaign) => {
        const getGameBadge = (type) => {
            switch (type) {
                case 'slot': return { label: 'Slot Machine', icon: '🎰', bg: 'bg-amber-50 text-amber-800 border-amber-300' };
                case 'box': return { label: 'Mystery Box', icon: '🎁', bg: 'bg-purple-50 text-purple-800 border-purple-300' };
                case 'scratch': return { label: 'Scratch Card', icon: '🎟️', bg: 'bg-emerald-50 text-emerald-800 border-emerald-300' };
                case 'spin':
                default: return { label: 'Spin & Win', icon: '🎡', bg: 'bg-indigo-50 text-indigo-800 border-indigo-300' };
            }
        };
        const badge = getGameBadge(campaign.campaign_type);

        return (
        <div
            key={campaign.id}
            className={`bg-white p-5 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all ${
                campaign.scheduled_for_deletion ? 'border-2 border-red-500 bg-red-50' : ''
            }`}
        >
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
                <div className="flex-1">
                    <div className="flex items-center gap-2.5 flex-wrap mb-1">
                        <h3 className="text-base sm:text-lg font-bold text-gray-900">{campaign.name}</h3>
                        <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-0.5 rounded-full border ${badge.bg}`}>
                            <span>{badge.icon}</span> {badge.label}
                        </span>
                        {campaign.is_in_store && (
                            <span className="text-[11px] font-bold bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full border border-gray-200">
                                🏬 In-Store Mode
                            </span>
                        )}
                    </div>
                    {campaign.scheduled_for_deletion && (
                        <div className="flex items-center gap-2 mt-1">
                            <span className="text-sm text-red-600 font-semibold">
                                Deleting in:
                            </span>
                            <CountdownTimer
                                scheduledTime={campaign.scheduled_for_deletion}
                                onExpire={fetchCampaigns}
                            />
                        </div>
                    )}
                </div>
                <div className="flex flex-wrap gap-2">
                    {campaign.scheduled_for_deletion ? (
                        <button
                            onClick={() => handleCancelDeletion(campaign.id)}
                            className="bg-orange-500 text-white px-3 py-1.5 rounded-lg text-xs sm:text-sm font-semibold hover:bg-orange-600"
                        >
                            Cancel Deletion
                        </button>
                    ) : (
                        <>
                            <button
                                onClick={() => {
                                    setEditingCampaign({
                                        ...campaign,
                                        start_date: convertUTCToLocal(campaign.start_date),
                                        end_date: convertUTCToLocal(campaign.end_date)
                                    });
                                    setEditModalOpen(true);
                                }}
                                className="bg-gray-100 text-gray-700 hover:bg-gray-200 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-semibold transition-colors"
                            >
                                Edit
                            </button>
                            <button
                                onClick={() => handleManagePrizes(campaign)}
                                className="bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-semibold transition-colors"
                            >
                                🎁 Manage Prizes
                            </button>
                            <button
                                onClick={() => setSelectedCampaignForDesign(campaign)}
                                className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded-lg text-xs sm:text-sm font-semibold transition-colors shadow-xs flex items-center gap-1"
                            >
                                <Palette size={14} /> Design & Branding
                            </button>
                            <button
                                onClick={() => setSelectedCampaignForQR(campaign)}
                                className="bg-emerald-600 text-white px-3 py-1.5 rounded-lg text-xs sm:text-sm font-semibold hover:bg-emerald-700 transition-colors shadow-xs flex items-center gap-1"
                            >
                                🚀 Integrate & QR
                            </button>
                            <button
                                onClick={() => handleDeleteCampaign(campaign.id)}
                                className="text-red-400 hover:text-red-600 p-1.5 rounded-lg hover:bg-red-50 transition-colors"
                                title="Delete Campaign"
                            >
                                <Trash2 size={16} />
                            </button>
                        </>
                    )}
                </div>
            </div>
            <div className="mt-3 text-xs text-gray-500 flex flex-wrap gap-x-4 gap-y-1">
                <p><strong>Start:</strong> {new Date(campaign.start_date).toLocaleDateString()}</p>
                <p><strong>End:</strong> {new Date(campaign.end_date).toLocaleDateString()}</p>
                <p><strong>Max Claims:</strong> {campaign.max_claims || 'Unlimited'}</p>
                <p><strong>Max Plays / IP:</strong> <span className="font-semibold text-gray-700">{campaign.max_spins_per_ip ?? 50}</span></p>
                {campaign.design_settings?.theme_style && (
                    <p><strong>Theme:</strong> <span className="capitalize font-medium text-gray-700">{campaign.design_settings.theme_style}</span></p>
                )}
            </div>
        </div>
        );
    })}
</div>

            {selectedCampaignForQR && (
                <CampaignIntegrationModal
                    campaign={selectedCampaignForQR}
                    onClose={() => setSelectedCampaignForQR(null)}
                />
            )}

            {selectedCampaignForDesign && (
                <CampaignDesignModal
                    campaign={selectedCampaignForDesign}
                    onClose={() => setSelectedCampaignForDesign(null)}
                    onUpdated={() => {
                        fetchCampaigns();
                        setSelectedCampaignForDesign(null);
                    }}
                />
            )}

            {/* Create Modal */}
            {isCreateModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center px-4">
                    <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] flex flex-col">
                       <div className="flex justify-between items-center p-4 sm:p-6 border-b">
                        <h2 className="text-lg sm:text-xl font-bold">Create New Campaign</h2>
                        <button
                            onClick={() => setIsCreateModalOpen(false)}
                            className="text-gray-500 hover:text-gray-700 text-2xl font-bold leading-none"
                            aria-label="Close modal"
                        >
                            ×
                        </button>
                        </div>

                        <div className="p-4 sm:p-6 overflow-y-auto flex-1">
                            <form onSubmit={handleCreateCampaign}>
                                <div className="mb-4">
                                    <label className="block mb-2">Name</label>
                                    <input
                                        type="text"
                                        value={newCampaign.name}
                                        onChange={e => setNewCampaign({...newCampaign, name: e.target.value})}
                                        className="w-full p-2 border rounded"
                                    />
                                </div>
                                <div className="mb-4">
                                    <label className="block mb-2 font-medium">Game Type</label>
                                    <select
                                        value={newCampaign.campaign_type}
                                        onChange={e => setNewCampaign({...newCampaign, campaign_type: e.target.value})}
                                        className="w-full p-2.5 border rounded-lg font-medium text-gray-800"
                                    >
                                        <option value="spin">🎡 Spin and Win (Lucky Wheel)</option>
                                        <option value="scratch">🎟️ Scratch Card (Instant Scratch Off)</option>
                                        <option value="slot">🎰 Slot Machine (3-Reel Jackpot)</option>
                                        <option value="box">🎁 Mystery Gift Box (Tap to Unbox)</option>
                                    </select>
                                </div>
                                <div className="mb-4">
                                    <label className="block mb-2">Start Date</label>
                                    <input
                                        type="datetime-local"
                                        value={newCampaign.start_date}
                                        onChange={e => setNewCampaign({...newCampaign, start_date: e.target.value})}
                                        className="w-full p-2 border rounded"
                                    />
                                </div>
                                <div className="mb-4">
                                    <label className="block mb-2">End Date</label>
                                    <input
                                        type="datetime-local"
                                        value={newCampaign.end_date}
                                        onChange={e => setNewCampaign({...newCampaign, end_date: e.target.value})}
                                        className="w-full p-2 border rounded"
                                    />
                                </div>
                                <div className="mb-4">
                                    <label className="flex items-center">
                                        <input
                                            type="checkbox"
                                            checked={newCampaign.is_in_store}
                                            onChange={e => setNewCampaign({...newCampaign, is_in_store: e.target.checked})}
                                            className="mr-2"
                                        />
                                        <div>
                                            <span className="font-medium">In-store Campaign</span>
                                            <p className="text-sm text-gray-500">Enable this if the campaign will be used at point of sale for different customers</p>
                                        </div>
                                    </label>
                                </div>
                                <div className="mb-4">
                                    <label className="block mb-2">Max Claims</label>
                                    <input
                                        type="number"
                                        value={newCampaign.max_claims}
                                        onChange={e => setNewCampaign({...newCampaign, max_claims: parseInt(e.target.value) || 0})}
                                        className="w-full p-2 border rounded"
                                    />
                                </div>
                                <div className="mb-4">
                                    <label className="block mb-1 font-medium text-sm text-gray-700">Max Plays per IP Address</label>
                                    <input
                                        type="number"
                                        value={newCampaign.max_spins_per_ip ?? 50}
                                        onChange={e => setNewCampaign({...newCampaign, max_spins_per_ip: parseInt(e.target.value) || 0})}
                                        className="w-full p-2 border rounded"
                                        min="1"
                                        max="10000"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">Limits total plays from the same IP address to prevent abuse (Default: 50)</p>
                                </div>
                                <div className="mb-4">
                                    <label className="flex items-center">
                                        <input
                                            type="checkbox"
                                            checked={newCampaign.show_social_page}
                                            onChange={e => setNewCampaign({...newCampaign, show_social_page: e.target.checked})}
                                            className="mr-2"
                                        />
                                        Show social media follow page before game
                                    </label>
                                </div>

                                {newCampaign.show_social_page && (
                                    <>
                                        <div className="mb-4">
                                            <label className="block mb-2">Instagram Link</label>
                                            <input
                                                type="url"
                                                value={newCampaign.instagram_link}
                                                onChange={e => setNewCampaign({...newCampaign, instagram_link: e.target.value})}
                                                className="w-full p-2 border rounded"
                                                placeholder="https://instagram.com/yourpage"
                                            />
                                            <p className="text-xs text-gray-500 mt-1">Include https:// at the beginning</p>
                                        </div>

                                        <div className="mb-4">
                                            <label className="block mb-2">Facebook Link</label>
                                            <input
                                                type="url"
                                                value={newCampaign.facebook_link}
                                                onChange={e => setNewCampaign({...newCampaign, facebook_link: e.target.value})}
                                                className="w-full p-2 border rounded"
                                                placeholder="https://facebook.com/yourpage"
                                            />
                                            <p className="text-xs text-gray-500 mt-1">Include https:// at the beginning</p>
                                        </div>

                                        <div className="mb-4">
                                            <label className="block mb-2">Guidelines</label>
                                            <textarea
                                                value={newCampaign.guidelines}
                                                onChange={e => setNewCampaign({...newCampaign, guidelines: e.target.value})}
                                                className="w-full p-2 border rounded"
                                                rows="4"
                                                placeholder="Enter each guideline on a new line"
                                            />
                                        </div>

                                    </>
                                )}
                                <div className="flex justify-end gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setIsCreateModalOpen(false)}
                                        className="px-4 py-2 border rounded"
                                        disabled={isSubmitting}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 bg-blue-500 text-white rounded disabled:bg-blue-300 disabled:cursor-not-allowed"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? 'Creating...' : 'Create'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Prize Modal */}
            {selectedCampaign && (
                <PrizeModal 
                    campaign={selectedCampaign} 
                    onClose={() => setSelectedCampaign(null)}
                />
            )}


            {editModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center px-4">
                    <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] flex flex-col">
                        <div className="p-4 sm:p-6 border-b">
                            <h2 className="text-lg sm:text-xl font-bold">Edit Campaign</h2>
                        </div>

                        <div className="p-4 sm:p-6 overflow-y-auto flex-1">
                            <form onSubmit={handleEditCampaign}>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block mb-2">Name</label>
                                        <input
                                            type="text"
                                            value={editingCampaign.name}
                                            onChange={e => setEditingCampaign({
                                                ...editingCampaign,
                                                name: e.target.value
                                            })}
                                            className="w-full p-2 border rounded"
                                        />
                                    </div>
                                    <div>
                                        <label className="block mb-2 font-medium">Game Type</label>
                                        <select
                                            value={editingCampaign.campaign_type}
                                            onChange={e => setEditingCampaign({
                                                ...editingCampaign,
                                                campaign_type: e.target.value
                                            })}
                                            className="w-full p-2.5 border rounded-lg font-medium text-gray-800"
                                        >
                                            <option value="spin">🎡 Spin and Win (Lucky Wheel)</option>
                                            <option value="scratch">🎟️ Scratch Card (Instant Scratch Off)</option>
                                            <option value="slot">🎰 Slot Machine (3-Reel Jackpot)</option>
                                            <option value="box">🎁 Mystery Gift Box (Tap to Unbox)</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block mb-2">Start Date</label>
                                        <input
                                            type="datetime-local"
                                            value={editingCampaign.start_date}
                                            onChange={e => setEditingCampaign({
                                                ...editingCampaign,
                                                start_date: e.target.value
                                            })}
                                            className="w-full p-2 border rounded"
                                        />
                                    </div>
                                    <div>
                                        <label className="block mb-2">End Date</label>
                                        <input
                                            type="datetime-local"
                                            value={editingCampaign.end_date}
                                            onChange={e => setEditingCampaign({
                                                ...editingCampaign,
                                                end_date: e.target.value
                                            })}
                                            className="w-full p-2 border rounded"
                                        />
                                    </div>
                                    <div>
                                        <label className="block mb-2">Max Claims</label>
                                        <input
                                            type="number"
                                            value={editingCampaign.max_claims}
                                            onChange={e => setEditingCampaign({
                                                ...editingCampaign,
                                                max_claims: parseInt(e.target.value) || 0
                                            })}
                                            className="w-full p-2 border rounded"
                                        />
                                    </div>
                                    <div>
                                        <label className="block mb-1 font-medium text-sm text-gray-700">Max Plays per IP Address</label>
                                        <input
                                            type="number"
                                            value={editingCampaign.max_spins_per_ip ?? 50}
                                            onChange={e => setEditingCampaign({
                                                ...editingCampaign,
                                                max_spins_per_ip: parseInt(e.target.value) || 0
                                            })}
                                            className="w-full p-2 border rounded"
                                            min="1"
                                            max="10000"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">Limits total plays from the same IP address to prevent abuse (Default: 50)</p>
                                    </div>
                                    <div>
                                        <label className="flex items-center">
                                            <input
                                                type="checkbox"
                                                checked={editingCampaign.is_in_store || false}
                                                onChange={e => setEditingCampaign({
                                                    ...editingCampaign,
                                                    is_in_store: e.target.checked
                                                })}
                                                className="mr-2"
                                            />
                                            <div>
                                                <span className="font-medium">In-store Campaign</span>
                                                <p className="text-sm text-gray-500">Enable this if the campaign will be used at point of sale for different customers</p>
                                            </div>
                                        </label>
                                    </div>
                                    <div>
                                        <label className="flex items-center">
                                            <input
                                                type="checkbox"
                                                checked={editingCampaign.show_social_page || false}
                                                onChange={e => setEditingCampaign({
                                                    ...editingCampaign,
                                                    show_social_page: e.target.checked
                                                })}
                                                className="mr-2"
                                            />
                                            Show social media follow page before game
                                        </label>
                                    </div>
                                    {editingCampaign.show_social_page && (
                                        <>
                                            <div>
                                                <label className="block mb-2">Instagram Link</label>
                                                <input
                                                    type="url"
                                                    value={editingCampaign.instagram_link || ''}
                                                    onChange={e => setEditingCampaign({
                                                        ...editingCampaign,
                                                        instagram_link: e.target.value
                                                    })}
                                                    className="w-full p-2 border rounded"
                                                    placeholder="https://instagram.com/yourpage"
                                                />
                                                <p className="text-xs text-gray-500 mt-1">Include https:// at the beginning</p>
                                            </div>
                                            <div>
                                                <label className="block mb-2">Facebook Link</label>
                                                <input
                                                    type="url"
                                                    value={editingCampaign.facebook_link || ''}
                                                    onChange={e => setEditingCampaign({
                                                        ...editingCampaign,
                                                        facebook_link: e.target.value
                                                    })}
                                                    className="w-full p-2 border rounded"
                                                    placeholder="https://facebook.com/yourpage"
                                                />
                                                <p className="text-xs text-gray-500 mt-1">Include https:// at the beginning</p>
                                            </div>
                                            <div>
                                                <label className="block mb-2">Guidelines</label>
                                                <textarea
                                                    value={editingCampaign.guidelines || ''}
                                                    onChange={e => setEditingCampaign({
                                                        ...editingCampaign,
                                                        guidelines: e.target.value
                                                    })}
                                                    className="w-full p-2 border rounded"
                                                    rows="4"
                                                    placeholder="Enter each guideline on a new line"
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>
                            </form>
                        </div>

                        <div className="p-4 sm:p-6 border-t bg-gray-50">
                            <div className="flex justify-end gap-2">
                                <button
                                    onClick={() => {
                                        setEditModalOpen(false);
                                        setEditingCampaign(null);
                                    }}
                                    className="px-3 sm:px-4 py-2 text-sm sm:text-base text-gray-600 hover:text-gray-800"
                                    disabled={isSubmitting}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleEditCampaign}
                                    className="px-3 sm:px-4 py-2 text-sm sm:text-base bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-blue-300 disabled:cursor-not-allowed"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
        </div>
    );
};

export default CampaignsPage;